// The workspace's one slot for questions components queue for the assistant
// (`pendingChatMessage`). The composer under the sphere sends whatever lands
// there. A waiting question is never replaced: a producer either fills an
// empty slot or waits for it, and only the composer empties it, by taking
// what is in it. A taken question holds the slot (`sendingChatMessage`) until
// the composer settles it: sent once its turn starts, dropped, or put back
// first when its turn never started, so no other question can take its place
// meanwhile. Every workspace store builds its slot actions here, so the rule
// lives in one place. This module never reads the shell host: the app's own
// store builds its slot here before any host is installed. Asking through
// the host's workspace lives in `host/chatSlot.ts`.

import type { ShellPendingChatMessage } from "../host/ports.ts";

/** A question queued for the assistant. */
export type ShellChatQuestion = NonNullable<ShellPendingChatMessage>;

/** Where a queued question stands; `unknown` for one this slot never held. */
export type ShellChatQuestionStatus = "queued" | "sending" | "sent" | "dropped" | "unknown";

export type ShellChatSlotState = {
  /** The question waiting to be sent, or null. */
  pendingChatMessage: ShellPendingChatMessage;
  /** The question the composer took and is sending, or null. It holds the slot. */
  sendingChatMessage: ShellPendingChatMessage;
};

export type ShellChatSlotActions = {
  /** Fills the slot if it is free. False, and nothing changes, when a question is waiting or being sent. */
  queuePendingChatMessage: (message: ShellChatQuestion) => boolean;
  /** Takes the waiting question to send it; it holds the slot until settled. Only the composer calls this. */
  takePendingChatMessage: () => ShellPendingChatMessage;
  /** The composer's taken question was sent (its turn started), or can never be: frees the slot. */
  settlePendingChatMessage: (message: ShellChatQuestion, outcome: "sent" | "dropped") => void;
  /** The composer's taken question never started: it goes back to wait first, ahead of any other. */
  returnPendingChatMessage: (message: ShellChatQuestion) => void;
  /** Takes a question that is still waiting back out of the slot. False once the composer took it. */
  withdrawPendingChatMessage: (message: ShellChatQuestion) => boolean;
  /** Where `message` stands. */
  chatQuestionStatus: (message: ShellChatQuestion) => ShellChatQuestionStatus;
};

/** The slot actions for a store, given its getter and setter. */
export function chatSlotActions(
  get: () => ShellChatSlotState,
  set: (patch: Partial<ShellChatSlotState>) => void,
): ShellChatSlotActions {
  const settled = new WeakMap<ShellChatQuestion, "sent" | "dropped">();
  return {
    queuePendingChatMessage(message) {
      const state = get();
      if (state.pendingChatMessage !== null || state.sendingChatMessage) return false;
      settled.delete(message);
      set({ pendingChatMessage: message });
      return true;
    },
    takePendingChatMessage() {
      const message = get().pendingChatMessage;
      if (message !== null) set({ pendingChatMessage: null, sendingChatMessage: message });
      return message;
    },
    settlePendingChatMessage(message, outcome) {
      if (get().sendingChatMessage !== message) return;
      settled.set(message, outcome);
      set({ sendingChatMessage: null });
    },
    returnPendingChatMessage(message) {
      if (get().sendingChatMessage !== message) return;
      set({ pendingChatMessage: message, sendingChatMessage: null });
    },
    withdrawPendingChatMessage(message) {
      if (get().pendingChatMessage !== message) return false;
      settled.set(message, "dropped");
      set({ pendingChatMessage: null });
      return true;
    },
    chatQuestionStatus(message) {
      const state = get();
      if (state.pendingChatMessage === message) return "queued";
      if (state.sendingChatMessage === message) return "sending";
      return settled.get(message) ?? "unknown";
    },
  };
}

type SlotStore = {
  getState: () => ShellChatSlotState & Pick<
    ShellChatSlotActions,
    "queuePendingChatMessage" | "withdrawPendingChatMessage" | "chatQuestionStatus"
  >;
  subscribe: (listener: () => void) => () => void;
};

export type QueueChatMessageOptions = {
  /** Stops waiting. A question not yet taken by the composer is then taken back out of the slot. */
  signal?: AbortSignal;
  /** A further condition the question waits for before it is queued (for example, no request running). */
  ready?: () => boolean;
  /** Tells the wait when `ready` may have changed. */
  subscribeReady?: (listener: () => void) => () => void;
  /**
   * Resolve only once the composer sent the question (the assistant claimed
   * it and its turn started; the answer may still be running), not as soon
   * as it is queued. False when it was dropped or `signal`
   * aborted first.
   */
  untilSent?: boolean;
};

/**
 * Queues `message` as soon as the slot is free (and `ready` holds), never
 * over a question already waiting or being sent. Resolves true once queued
 * (or, with `untilSent`, once sent), false when `signal` aborted first.
 */
export function queueChatMessageWhenFree(
  store: SlotStore,
  message: ShellChatQuestion,
  { signal, ready, subscribeReady, untilSent = false }: QueueChatMessageOptions = {},
): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let done = false;
    let queued = false;
    let stopStore: () => void = () => {};
    let stopReady: () => void = () => {};
    const finish = (result: boolean) => {
      if (done) return;
      done = true;
      stopStore();
      stopReady();
      signal?.removeEventListener("abort", check);
      resolve(result);
    };
    function check() {
      if (done) return;
      const state = store.getState();
      if (signal?.aborted) {
        if (queued) state.withdrawPendingChatMessage(message);
        return finish(false);
      }
      if (queued) {
        const status = state.chatQuestionStatus(message);
        if (status === "sent") finish(true);
        else if (status === "dropped" || status === "unknown") finish(false);
        return;
      }
      if (ready && !ready()) return;
      if (!state.queuePendingChatMessage(message)) return;
      queued = true;
      if (!untilSent) finish(true);
    }
    stopStore = store.subscribe(check);
    if (subscribeReady) stopReady = subscribeReady(check);
    signal?.addEventListener("abort", check, { once: true });
    check();
  });
}
